import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Copy, Gift, Link2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface InviteFriendsDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Invite friends and earn free credits" — the in-app referral program
 * dialog. Rewards land in the referrer's music-video credit wallet:
 * 250 credits when a referred friend completes + exports their first video
 * (capped), 500 credits when they make their first purchase.
 */
export default function InviteFriendsDialog({
  open,
  onClose,
}: InviteFriendsDialogProps) {
  const [copied, setCopied] = useState(false);

  const summaryQuery = trpc.referral.summary.useQuery(undefined, {
    enabled: open,
    staleTime: 60_000,
  });
  const summary = summaryQuery.data;

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const shareLink = summary?.code
    ? `${window.location.origin}/?ref=${summary.code}`
    : "";

  const handleCopy = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link — copy it manually instead");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md rounded-2xl border border-white/10 bg-[#202029] p-6 text-white">
        <DialogTitle className="text-center text-xl font-bold">
          Invite friends and earn free credits
        </DialogTitle>

        <p className="mt-1 text-sm text-white/70">
          When friends sign up to Solmi through your referral link:
        </p>

        <div className="mt-3 space-y-4">
          <div className="flex items-start gap-3">
            <Gift className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            <div>
              <p className="text-sm font-semibold">
                {summary?.exportRewardCredits ?? 100} music video credits for
                you
              </p>
              <p className="mt-0.5 text-xs leading-snug text-white/50">
                When a friend generates a complete music video and exports it
                for the first time (max {summary?.exportRewardCap ?? 10}{" "}
                rewards)
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Gift className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            <div>
              <p className="text-sm font-semibold">
                {summary?.purchaseRewardCredits ?? 300} music video credits for
                you
              </p>
              <p className="mt-0.5 text-xs leading-snug text-white/50">
                When they subscribe to a paid plan or buy credits for the
                first time
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <p className="mb-2 text-sm text-white/70">My sharing link</p>
          <div className="flex items-center gap-2 rounded-full bg-white/[0.06] py-1.5 pl-4 pr-1.5">
            <Link2 className="h-4 w-4 shrink-0 text-white/50" />
            {summaryQuery.isLoading ? (
              <span className="flex flex-1 items-center gap-2 text-sm text-white/50">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </span>
            ) : (
              <span
                className="min-w-0 flex-1 truncate text-sm text-white/90"
                title={shareLink}
              >
                {shareLink || "Could not load your link — try again"}
              </span>
            )}
            <Button
              type="button"
              onClick={handleCopy}
              disabled={!shareLink}
              className="h-9 shrink-0 rounded-full px-4"
            >
              {copied ? (
                <>
                  <Check className="mr-1 h-4 w-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1 h-4 w-4" /> Copy Link
                </>
              )}
            </Button>
          </div>
        </div>

        {summary && (summary.referredCount > 0 || summary.creditsEarned > 0) ? (
          <p className="mt-3 text-center text-xs text-white/50">
            {summary.referredCount} friend
            {summary.referredCount === 1 ? "" : "s"} joined ·{" "}
            {summary.creditsEarned.toLocaleString()} credits earned
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
